import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';
import { validateResourceUrl } from '../utils/validation.js';

export interface AlibabaWan25T2iPreviewParams {
  /**
   * 输入参数
   */
  input?: {
    /**
     * 正向提示词，长度不超过2000字符
     */
    prompt: string;
    /**
     * 负向提示词，长度不超过500字符
     */
    negative_prompt?: string;
  };
  /**
   * 生成参数
   */
  parameters?: {
    /**
     * 图像尺寸，总像素必须在[768*768, 1440*1440]范围内，宽高比在[1:4, 4:1]之间
     */
    size?: string;
    /**
     * 生成图像数量，范围1-4
     * @default 1
     * @range 1 - 4
     */
    n?: number;
    /**
     * 随机种子，范围0-2147483647
     */
    seed?: number;
    /**
     * 是否启用提示词扩展
     * @default false
     */
    prompt_extend?: boolean;
    /**
     * 是否添加水印
     * @default false
     */
    watermark?: boolean;
  };
}

export interface AlibabaWan25T2iPreviewResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * alibaba_wan25_t2i_preview
 *
 * Alibaba Wan2.5 T2I Preview Image Generator
 * 
 * Text-to-Image generation implementation for Alibaba Wanxiang 2.5 T2I Preview API.
 * 
 * Model: wan2.5-t2i-preview (固定模型)
 * Features: 文本生成图像功能
 * Image Count: 1-4张 (默认1张)
 * Size: 总像素必须在[768*768, 1440*1440]范围内，宽高比在[1:4, 4:1]之间
 * Advanced: 支持提示词扩展、水印设置、随机种子控制
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, alibabaWan25T2iPreview } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await alibabaWan25T2iPreview({
 *   // 参数见 AlibabaWan25T2iPreviewParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 AlibabaWan25T2iPreviewParams 接口定义
 * @returns 生成的资源列表
 */
export async function alibabaWan25T2iPreview(
  params: AlibabaWan25T2iPreviewParams,
): Promise<AlibabaWan25T2iPreviewResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'alibaba_wan25_t2i_preview',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: AlibabaWan25T2iPreviewResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          // Validate URL before adding to results
          validateResourceUrl(resource, 'image');

          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
